import {
  angleInArc,
  angleOf,
  closestPointOnSegment,
  cross,
  dist,
  eq,
  intersectLines,
  intersectSegmentCircle,
  pt,
  sub,
  unit,
  type Pt,
} from '../model/geom';
import {
  entitySegments,
  type ArcEntity,
  type BlockDef,
  type CadEntity,
  type CircleEntity,
  type LineEntity,
  type PolylineEntity,
} from '../model/entity';
import { normalizeAngle, type Micron } from '../model/units';

/**
 * OFFSET · TRIM · EXTEND 의 기하 (작업지시서 P2-11)
 *
 * 이 세 명령이 CAD 편집의 체감 품질을 결정한다. 매대 배치 실무에서
 * "벽에서 2.4m 띄우기(OFFSET)", "교차선 정리(TRIM)"는 하루에 수백 번 쓴다.
 *
 * 명령 본문과 분리해 순수 함수로 두었다 — 단위 테스트로 고정하기 위해서다.
 */

/* ========================================================================== *
 * OFFSET
 * ========================================================================== */

/**
 * 엔티티를 지정 거리만큼 평행 복사한다.
 * `side` 는 사용자가 클릭한 쪽 — 어느 방향으로 밀지 결정한다.
 */
export function offsetEntity(e: CadEntity, distance: Micron, side: Pt): CadEntity | null {
  if (distance <= 0) return null;

  switch (e.type) {
    case 'line':
      return offsetLine(e, distance, side);
    case 'polyline':
      return offsetPolyline(e, distance, side);
    case 'circle': {
      // 클릭한 쪽이 바깥이면 커지고, 안쪽이면 작아진다
      const outward = dist(side, e.center) > e.radius;
      const r = outward ? e.radius + distance : e.radius - distance;
      if (r <= 0) return null;
      return { ...e, radius: Math.round(r) };
    }
    case 'arc': {
      const outward = dist(side, e.center) > e.radius;
      const r = outward ? e.radius + distance : e.radius - distance;
      if (r <= 0) return null;
      return { ...e, radius: Math.round(r) };
    }
    default:
      return null; // 문자·블록·치수는 평행 복사 대상이 아니다
  }
}

/** 선분의 왼쪽 법선(단위 벡터) */
function leftNormal(a: Pt, b: Pt): { x: number; y: number } {
  const d = unit(sub(b, a));
  return { x: -d.y, y: d.x };
}

/** 점이 선분의 어느 쪽인가. +1 = 왼쪽, -1 = 오른쪽 */
function sideSign(a: Pt, b: Pt, p: Pt): number {
  const c = cross(sub(b, a), sub(p, a));
  return c >= 0 ? 1 : -1;
}

function offsetLine(e: LineEntity, d: Micron, side: Pt): LineEntity {
  const n = leftNormal(e.a, e.b);
  const s = sideSign(e.a, e.b, side);
  const dx = n.x * d * s;
  const dy = n.y * d * s;
  return {
    ...e,
    a: pt(e.a.x + dx, e.a.y + dy),
    b: pt(e.b.x + dx, e.b.y + dy),
  };
}

/**
 * 폴리선 평행 복사.
 * 각 선분을 밀어낸 뒤 **이웃한 선분끼리 교차시켜** 마이터 조인을 만든다.
 * 단순히 각 정점을 법선 방향으로 밀면 꺾인 부분이 벌어지거나 겹친다.
 */
function offsetPolyline(e: PolylineEntity, d: Micron, side: Pt): PolylineEntity | null {
  const src = e.points;
  if (src.length < 2) return null;

  // 클릭 지점과 가장 가까운 선분을 기준으로 방향을 정한다
  let bestSeg = 0;
  let bestDist = Infinity;
  for (let i = 0; i + 1 < src.length; i++) {
    const dd = dist(side, closestPointOnSegment(side, src[i]!, src[i + 1]!));
    if (dd < bestDist) {
      bestDist = dd;
      bestSeg = i;
    }
  }
  const s = sideSign(src[bestSeg]!, src[bestSeg + 1]!, side);

  // 1) 각 선분을 통째로 평행 이동
  const segs: [Pt, Pt][] = [];
  const count = e.closed ? src.length : src.length - 1;
  for (let i = 0; i < count; i++) {
    const a = src[i]!;
    const b = src[(i + 1) % src.length]!;
    if (eq(a, b)) continue;
    const n = leftNormal(a, b);
    segs.push([
      pt(a.x + n.x * d * s, a.y + n.y * d * s),
      pt(b.x + n.x * d * s, b.y + n.y * d * s),
    ]);
  }
  if (segs.length === 0) return null;

  // 2) 이웃 선분을 무한 직선으로 교차시켜 꼭짓점을 만든다
  const out: Pt[] = [];
  if (!e.closed) out.push(segs[0]![0]);

  for (let i = 0; i < segs.length; i++) {
    const cur = segs[i]!;
    const next = segs[(i + 1) % segs.length];
    const isLast = i === segs.length - 1;

    if (!e.closed && isLast) {
      out.push(cur[1]);
      break;
    }
    if (!next) {
      out.push(cur[1]);
      continue;
    }

    const x = intersectLines(cur[0], cur[1], next[0], next[1], false);
    // 평행(=일직선)이면 교점이 없다. 그때는 끝점을 그대로 쓴다.
    out.push(x ?? cur[1]);
  }

  if (e.closed && out.length > 0) {
    // 닫힌 폴리선은 마지막 교점이 첫 꼭짓점이 되므로 앞으로 회전시킨다
    out.unshift(out.pop()!);
  }

  return { ...e, points: out };
}

/* ========================================================================== *
 * TRIM
 * ========================================================================== */

export interface TrimResult {
  /** 잘려서 사라지는 원본 */
  removed: CadEntity;
  /** 잘린 뒤 남는 조각들. 비면 통째로 삭제된다. */
  added: CadEntity[];
}

/**
 * 절단 모서리(cutters)를 기준으로 target 에서 클릭한 구간을 잘라낸다.
 *
 * @param clickPoint 사용자가 "여기를 지워라"고 찍은 점
 * @param newId      새 조각에 부여할 id 발급기
 */
export function trimEntity(
  target: CadEntity,
  cutters: CadEntity[],
  clickPoint: Pt,
  newId: () => string,
  blocks?: Map<string, BlockDef>
): TrimResult | null {
  switch (target.type) {
    case 'line':
      return trimLine(target, cutters, clickPoint, newId, blocks);
    case 'polyline':
      return trimPolyline(target, cutters, clickPoint, newId, blocks);
    case 'circle':
      return trimCircle(target, cutters, clickPoint, newId, blocks);
    case 'arc':
      return trimArc(target, cutters, clickPoint, newId, blocks);
    default:
      return null;
  }
}

/** target 을 자르는 모든 교점 (자기 자신은 제외) */
function cutPointsOn(
  a: Pt,
  b: Pt,
  cutters: CadEntity[],
  targetId: string,
  blocks?: Map<string, BlockDef>
): Pt[] {
  const out: Pt[] = [];
  for (const c of cutters) {
    if (c.id === targetId) continue;
    for (const [c1, c2] of entitySegments(c, blocks)) {
      const x = intersectLines(a, b, c1, c2, true);
      if (x) out.push(x);
    }
  }
  return out;
}

/** 점의 선분 상 매개변수 t (0=a, 1=b) */
function paramOn(a: Pt, b: Pt, p: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return 0;
  return ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
}

function trimLine(
  e: LineEntity,
  cutters: CadEntity[],
  click: Pt,
  newId: () => string,
  blocks?: Map<string, BlockDef>
): TrimResult | null {
  const cuts = cutPointsOn(e.a, e.b, cutters, e.id, blocks)
    .map((p) => paramOn(e.a, e.b, p))
    .filter((t) => t > 1e-6 && t < 1 - 1e-6)
    .sort((x, y) => x - y);

  if (cuts.length === 0) return null;

  const tc = paramOn(e.a, e.b, click);
  const bounds = [0, ...cuts, 1];

  // 클릭이 들어 있는 구간을 찾는다
  let lo = 0;
  let hi = 1;
  for (let i = 0; i + 1 < bounds.length; i++) {
    if (tc >= bounds[i]! && tc <= bounds[i + 1]!) {
      lo = bounds[i]!;
      hi = bounds[i + 1]!;
      break;
    }
  }

  const at = (t: number): Pt => pt(e.a.x + (e.b.x - e.a.x) * t, e.a.y + (e.b.y - e.a.y) * t);
  const added: CadEntity[] = [];

  if (lo > 1e-6) added.push({ ...e, id: newId(), a: e.a, b: at(lo) });
  if (hi < 1 - 1e-6) added.push({ ...e, id: newId(), a: at(hi), b: e.b });

  return { removed: e, added };
}

function trimPolyline(
  e: PolylineEntity,
  cutters: CadEntity[],
  click: Pt,
  newId: () => string,
  blocks?: Map<string, BlockDef>
): TrimResult | null {
  const pts = e.points;
  const segCount = e.closed ? pts.length : pts.length - 1;
  if (segCount < 1) return null;

  const segAt = (i: number): [Pt, Pt] => [pts[i]!, pts[(i + 1) % pts.length]!];

  /** 전역 매개변수 = 선분 인덱스 + 선분 내 t */
  const cutParams: number[] = [];
  for (let i = 0; i < segCount; i++) {
    const [a, b] = segAt(i);
    for (const x of cutPointsOn(a, b, cutters, e.id, blocks)) {
      const t = paramOn(a, b, x);
      if (t > 1e-6 && t < 1 - 1e-6) cutParams.push(i + t);
    }
  }
  if (cutParams.length === 0) return null;
  cutParams.sort((x, y) => x - y);

  // 클릭 위치의 전역 매개변수
  let clickParam = 0;
  let bestD = Infinity;
  for (let i = 0; i < segCount; i++) {
    const [a, b] = segAt(i);
    const p = closestPointOnSegment(click, a, b);
    const d = dist(click, p);
    if (d < bestD) {
      bestD = d;
      clickParam = i + Math.max(0, Math.min(1, paramOn(a, b, p)));
    }
  }

  const maxParam = segCount;
  const bounds = [0, ...cutParams, maxParam];
  let lo = 0;
  let hi = maxParam;
  for (let i = 0; i + 1 < bounds.length; i++) {
    if (clickParam >= bounds[i]! && clickParam <= bounds[i + 1]!) {
      lo = bounds[i]!;
      hi = bounds[i + 1]!;
      break;
    }
  }

  const pointAt = (param: number): Pt => {
    const i = Math.min(segCount - 1, Math.floor(param));
    const t = param - i;
    const [a, b] = segAt(i);
    return pt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
  };

  /** [from, to] 구간의 정점 열 */
  const slice = (from: number, to: number): Pt[] => {
    const out: Pt[] = [pointAt(from)];
    for (let i = Math.ceil(from + 1e-9); i <= Math.floor(to - 1e-9); i++) {
      out.push(pts[i % pts.length]!);
    }
    const end = pointAt(to);
    if (out.length === 0 || !eq(out[out.length - 1]!, end)) out.push(end);
    return out;
  };

  const added: CadEntity[] = [];

  if (e.closed) {
    // 닫힌 폴리선에서 한 조각을 지우면 열린 폴리선 하나가 남는다
    const remain = slice(hi, maxParam).concat(slice(0, lo).slice(1));
    if (remain.length >= 2) {
      added.push({ ...e, id: newId(), points: remain, closed: false });
    }
  } else {
    if (lo > 1e-6) {
      const left = slice(0, lo);
      if (left.length >= 2) added.push({ ...e, id: newId(), points: left, closed: false });
    }
    if (hi < maxParam - 1e-6) {
      const right = slice(hi, maxParam);
      if (right.length >= 2) added.push({ ...e, id: newId(), points: right, closed: false });
    }
  }

  return { removed: e, added };
}

function trimCircle(
  e: CircleEntity,
  cutters: CadEntity[],
  click: Pt,
  newId: () => string,
  blocks?: Map<string, BlockDef>
): TrimResult | null {
  const angles: number[] = [];
  for (const c of cutters) {
    if (c.id === e.id) continue;
    for (const [c1, c2] of entitySegments(c, blocks)) {
      for (const x of intersectSegmentCircle(c1, c2, e.center, e.radius, true)) {
        angles.push(normalizeAngle(angleOf(e.center, x)));
      }
    }
  }
  if (angles.length < 2) return null;
  angles.sort((a, b) => a - b);

  const ca = normalizeAngle(angleOf(e.center, click));

  // 클릭 각도를 감싸는 두 교점을 찾는다
  let lo = angles[angles.length - 1]!;
  let hi = angles[0]!;
  for (let i = 0; i + 1 < angles.length; i++) {
    if (ca >= angles[i]! && ca <= angles[i + 1]!) {
      lo = angles[i]!;
      hi = angles[i + 1]!;
      break;
    }
  }

  // 원에서 [lo, hi] 를 지우면 hi → lo 로 이어지는 호 하나가 남는다
  const arc: ArcEntity = {
    ...e,
    id: newId(),
    type: 'arc',
    startAngle: hi,
    endAngle: lo,
  };
  return { removed: e, added: [arc] };
}

function trimArc(
  e: ArcEntity,
  cutters: CadEntity[],
  click: Pt,
  newId: () => string,
  blocks?: Map<string, BlockDef>
): TrimResult | null {
  const start = normalizeAngle(e.startAngle);
  let sweep = normalizeAngle(e.endAngle) - start;
  if (sweep <= 0) sweep += Math.PI * 2;

  /** 각도를 호 시작 기준 0..sweep 로 */
  const rel = (a: number): number => {
    let r = normalizeAngle(a) - start;
    if (r < 0) r += Math.PI * 2;
    return r;
  };

  const cuts: number[] = [];
  for (const c of cutters) {
    if (c.id === e.id) continue;
    for (const [c1, c2] of entitySegments(c, blocks)) {
      for (const x of intersectSegmentCircle(c1, c2, e.center, e.radius, true)) {
        const a = angleOf(e.center, x);
        if (!angleInArc(a, e.startAngle, e.endAngle)) continue;
        const r = rel(a);
        if (r > 1e-9 && r < sweep - 1e-9) cuts.push(r);
      }
    }
  }
  if (cuts.length === 0) return null;
  cuts.sort((a, b) => a - b);

  const cr = rel(angleOf(e.center, click));
  const bounds = [0, ...cuts, sweep];
  let lo = 0;
  let hi = sweep;
  for (let i = 0; i + 1 < bounds.length; i++) {
    if (cr >= bounds[i]! && cr <= bounds[i + 1]!) {
      lo = bounds[i]!;
      hi = bounds[i + 1]!;
      break;
    }
  }

  const added: CadEntity[] = [];
  if (lo > 1e-9) {
    added.push({ ...e, id: newId(), startAngle: start, endAngle: normalizeAngle(start + lo) });
  }
  if (hi < sweep - 1e-9) {
    added.push({
      ...e,
      id: newId(),
      startAngle: normalizeAngle(start + hi),
      endAngle: normalizeAngle(start + sweep),
    });
  }
  return { removed: e, added };
}

/* ========================================================================== *
 * EXTEND
 * ========================================================================== */

/**
 * 경계선(boundaries)까지 선을 늘린다.
 * 클릭한 쪽에 가까운 끝점이 움직인다 — AutoCAD 와 같은 동작.
 */
export function extendEntity(
  target: CadEntity,
  boundaries: CadEntity[],
  clickPoint: Pt,
  blocks?: Map<string, BlockDef>
): CadEntity | null {
  if (target.type !== 'line') return null; // 호 연장은 P2 후반에서 다룬다

  const e = target;
  const nearA = dist(clickPoint, e.a) <= dist(clickPoint, e.b);

  // 늘어나는 방향: 고정단 → 이동단
  const fixed = nearA ? e.b : e.a;
  const moving = nearA ? e.a : e.b;

  const dir = unit(sub(moving, fixed));
  if (dir.x === 0 && dir.y === 0) return null;

  let bestT = Infinity;
  let bestPt: Pt | null = null;

  // 아주 먼 지점까지의 반직선으로 경계와 교차시킨다
  const far = pt(fixed.x + dir.x * 1e9, fixed.y + dir.y * 1e9);

  for (const b of boundaries) {
    if (b.id === e.id) continue;
    for (const [b1, b2] of entitySegments(b, blocks)) {
      // 반직선(고정단→무한) 과 경계 선분의 교차
      const x = intersectLines(fixed, far, b1, b2, true);
      if (!x) continue;

      const t = paramOn(fixed, far, x);
      const tMoving = paramOn(fixed, far, moving);
      // 현재 끝점보다 더 먼 교점만 의미가 있다 (늘리는 것이므로)
      if (t <= tMoving + 1e-12) continue;
      if (t < bestT) {
        bestT = t;
        bestPt = x;
      }
    }
  }

  if (!bestPt) return null;
  return nearA ? { ...e, a: bestPt } : { ...e, b: bestPt };
}
